import React, { useRef } from "react";
import styled from "styled-components";
import {
    PortalFormDescription,
    PortalFormFieldGroup,
    PortalFormLabel,
    StandardModalActionButton,
    portalColors
} from "src/components";
import Remove from "src/img/remove-hero-image.png";
import DocumentIcon from "src/img/emailCampaigns/document@2x.png";
import ImageIcon from "src/img/emailCampaigns/image@2x.png";
import VideoIcon from "src/img/emailCampaigns/video@2x.png";

const AttachmentList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-top: 0.75rem;
`;

const AttachmentItem = styled.div`
    min-width: 0;
    width: min(100%, 18rem);
    height: 46px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0 0.75rem;
    border: 1px solid ${portalColors.border};
    border-radius: 8px;
    background: ${portalColors.surfaceSoft};
    box-sizing: border-box;
`;

const AttachmentName = styled.span`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
`;

const AttachmentDetails = styled.div`
    min-width: 0;
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
`;

const RemoveButton = styled.button`
    width: 1.65rem;
    height: 1.65rem;
    padding: 0;
    border: 1px solid ${portalColors.text};
    border-radius: 999px;
    background: ${portalColors.surface};
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;

    img {
        width: 1.45rem;
        height: 1.45rem;
    }
`;

export default function Attachments(props) {
    const { onAttachmentsChange, attachments, setAttachments } = props;
    const fileInputRef = useRef(null);

    const handleUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result;
            onAttachmentsChange(file, result);
        };
        reader.readAsDataURL(file);
    };

    const onImportCsvClick = () => {
        fileInputRef.current.click();
    };
    const TruncatedText = (text, maxLength) => {
        if (!text || text.length <= maxLength) {
            return <span>{text}</span>;
        }

        const dotIndex = text.lastIndexOf(".");
        if (dotIndex === -1) {
            return <span>{text.substring(0, maxLength)}...</span>;
        }

        const extension = text.substring(dotIndex);
        const name = text.substring(0, dotIndex);

        const ellipsis = "...";

        const available = maxLength - extension.length - ellipsis.length;

        if (available <= 0) {
            return <span>{text}</span>;
        }

        const startLength = Math.ceil(available / 2);
        const endLength = Math.floor(available / 2);

        const start = name.substring(0, startLength);
        const end = name.substring(name.length - endLength);

        const displayText = `${start}${ellipsis}${end}${extension}`;

        return <span>{displayText}</span>;
    };

    const onRemoveAttachment = (index) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
    };

    const attachmentIcon = (filename) => {
        if (!filename) return null;

        const ext = filename.split(".").pop().toLowerCase();

        const documentExtensions = [
            "pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx",
            "txt", "csv", "rtf"
        ];

        const imageExtensions = [
            "jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"
        ];

        const videoExtensions = [
            "mp4", "mov", "avi", "mkv", "webm"
        ];

        if (documentExtensions.includes(ext)) {
            return DocumentIcon;
        }

        if (imageExtensions.includes(ext)) {
            return ImageIcon;
        }

        if (videoExtensions.includes(ext)) {
            return VideoIcon;
        }

        return DocumentIcon;
    };

    return (
        <PortalFormFieldGroup>
            <PortalFormLabel>
                Attachments
            </PortalFormLabel>
            <PortalFormDescription>
                Add any files or documents you want to include with this email.
            </PortalFormDescription>
            <input
                type="file"
                accept="*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleUpload}
            />
            <StandardModalActionButton
                type="button"
                $variant="primary"
                onClick={onImportCsvClick}
            >
                Browse File
            </StandardModalActionButton>
            <AttachmentList>
                {attachments?.length > 0 &&
                    attachments?.map((file, index) => {
                        return (
                            <AttachmentItem
                                key={index}
                            >
                                <AttachmentDetails>
                                    <img
                                        src={attachmentIcon(file?.name ? file?.name : file?.filename)}
                                        height="25px"
                                        width="25px"
                                        style={{ objectFit: "fill" }}
                                    />
                                    <AttachmentName>
                                        {TruncatedText(file?.name || file?.filename || "(Error)", 20)}
                                    </AttachmentName>
                                </AttachmentDetails>
                                <RemoveButton type="button" onClick={() => onRemoveAttachment(index)}>
                                    <img src={Remove} alt="" />
                                </RemoveButton>
                            </AttachmentItem>
                        );
                    })}
            </AttachmentList>
        </PortalFormFieldGroup>
    );
}
